import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Plus, MessageCircle, Check } from "lucide-react";
import { login } from "@/lib/api";

export default function Scenes() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [scenes, setScenes] = useState([]);
  const [reels, setReels] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/scenes").then(r => setScenes(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/reels").then(r => setReels(r.data)).catch(() => {});
    if (user) load();
  }, [user]);

  const toggle = (story_id) => {
    setSelected((cur) =>
      cur.includes(story_id) ? cur.filter(x => x !== story_id) : cur.length >= 5 ? cur : [...cur, story_id]
    );
  };

  const create = async (e) => {
    e.preventDefault();
    if (selected.length < 2) { toast.error("Pick at least 2 characters"); return; }
    if (!title.trim() || !prompt.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/scenes", {
        title, prompt,
        characters: selected.map(story_id => ({ story_id })),
      });
      toast.success("Scene created — the room is open");
      setOpen(false); setTitle(""); setPrompt(""); setSelected([]);
      nav(`/scenes/${data.scene_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <Users className="h-10 w-10 text-rose-400 mx-auto mb-4" />
        <h1 className="font-serif text-3xl font-bold mb-3">Sign in to open a Group Scene</h1>
        <p className="text-slate-400 mb-6">Bring two or more characters from any story into one room.</p>
        <Button onClick={login} className="rounded-full bg-amber-500 text-black font-semibold px-7 h-12">Sign in</Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-rose-400 mb-2">Group Scenes</div>
          <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight">Two worlds. One room.</h1>
          <p className="mt-3 text-slate-400 max-w-2xl">Cross-cast characters from any story. Set a scene. Watch them talk. Jump in whenever you like.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-scene-btn" className="bg-gradient-to-r from-rose-500 to-amber-500 hover:opacity-90 text-black font-semibold rounded-full h-11 px-5">
              <Plus className="h-4 w-4 mr-1" /> New scene
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F1117] border-white/10 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Start a Group Scene</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">Pick 2–5 characters. Set the scene. Claude will direct the room.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Scene title</Label>
                <Input data-testid="scene-title" value={title} onChange={e => setTitle(e.target.value)} maxLength={120} placeholder="e.g. Two witches walk into a diner" className="mt-1 bg-[#07080A] border-white/10" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Opening setup</Label>
                <Textarea data-testid="scene-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} maxLength={600} rows={3} placeholder="Describe where they meet, what's at stake, and what's about to happen." className="mt-1 bg-[#07080A] border-white/10" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Choose characters ({selected.length}/5)</Label>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
                  {reels.map(r => {
                    const prot = r.protagonist || {};
                    const on = selected.includes(r.story_id);
                    return (
                      <button
                        type="button"
                        key={r.story_id}
                        data-testid={`pick-${r.story_id}`}
                        onClick={() => toggle(r.story_id)}
                        className={`text-left rounded-xl border p-3 transition ${on ? "border-amber-500/70 bg-amber-500/10" : "border-white/10 bg-[#07080A] hover:border-white/20"}`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            aria-hidden="true"
                            className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? "bg-amber-500 border-amber-400 text-black" : "border-white/20 bg-transparent"}`}
                          >
                            {on && <Check className="h-3 w-3" strokeWidth={3} />}
                          </span>
                          <div className="min-w-0">
                            <div className="font-serif text-sm font-semibold truncate">{prot.name || "Protagonist"}</div>
                            <div className="text-[10px] text-slate-500 truncate">{r.title}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <Button data-testid="scene-submit" type="submit" disabled={busy || selected.length < 2} className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11">
                {busy ? "Opening the room…" : "Open the scene"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {scenes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-slate-500">
          No scenes yet — start your first one to hear two characters meet.
        </div>
      ) : (
        <div data-testid="scenes-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {scenes.map((s) => (
            <Link key={s.scene_id} to={`/scenes/${s.scene_id}`} data-testid={`scene-card-${s.scene_id}`} className="rounded-2xl border border-white/10 bg-[#0F1117] p-6 glow-hover block">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-rose-400 flex items-center gap-1"><Users className="h-3 w-3" /> {s.characters.length} cast</div>
                <div className="text-[10px] text-slate-500 font-mono">{new Date(s.updated_at).toLocaleDateString()}</div>
              </div>
              <h3 className="font-serif text-xl font-semibold mb-2 line-clamp-2">{s.title}</h3>
              <p className="text-sm text-slate-400 line-clamp-3">{s.prompt}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {s.characters.slice(0, 4).map((c) => (
                  <span key={c.name} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-300">{c.name}</span>
                ))}
              </div>
              <div className="mt-4 text-xs text-amber-300 inline-flex items-center gap-1">
                <MessageCircle className="h-3.5 w-3.5" /> Open room
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
